import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, createRef } from 'react';
import gsap from 'gsap';
import useMount from './useMount';
import type { ImageSliderItemRef } from 'components/blocks/image-slider-draggable/image-slider-item/ImageSliderItem.data';

export function useImageParallax(
  enable: boolean,
  itemsCount: number = 0,
): {
  itemsRef: RefObject<Array<ImageSliderItemRef> | null>;
  setCurrentPosition: (position: number) => void;
} {
  const imageScale = 1.1;
  const distance = -(imageScale - 1) * 100;
  // const [positions, setPositions] = useState<Array<{ left: number; right: number }>>([]);
  // const [currentPosition, setCurrentPosition] = useState<number>(0);
  const itemsRef = useRef<Array<ImageSliderItemRef>>(
    new Array(itemsCount).fill(0).fill(createRef()),
  );
  const currentPosition = useRef<number>(0);

  const positions = useRef<Array<{ left: number; right: number }>>(undefined);
  const setPositions = useCallback((values: Array<{ left: number; right: number }>) => {
    positions.current = values;
  }, []);

  useMount(() => {
    if (enable) {
      const positions = itemsRef?.current?.map((item) => ({
        left: item?.elementRef?.current?.offsetLeft || 0,
        right:
          (item?.elementRef?.current?.offsetLeft || 0) +
          (item?.elementRef?.current?.offsetWidth || 0),
      }));

      setPositions(positions);

      itemsRef?.current?.forEach((item) => {
        gsap.set(item.imageRef.current, { scale: imageScale });
      });
    }
  });

  const updateImageParallax = useCallback(() => {
    positions?.current?.forEach((position, index) => {
      const inView =
        position.right > Math.abs(currentPosition.current) &&
        position.left - Math.abs(currentPosition.current) < window.innerWidth;

      if (inView && itemsRef?.current[index]?.imageRef?.current) {
        const width = position.right - position.left;
        const total = position.left + width;
        const x = Math.abs(currentPosition.current);
        const progress = x / total;

        gsap.set(itemsRef?.current[index]?.imageRef?.current as HTMLImageElement, {
          scale: imageScale,
          xPercent: distance / 2 + -distance * progress,
        });
      }
    });
  }, [itemsRef, distance]);

  const setCurrentPosition = useCallback(
    (position: number) => {
      currentPosition.current = position;

      if (enable) {
        updateImageParallax();
      }
    },
    [enable, updateImageParallax],
  );

  useEffect(() => {
    if (enable) {
      updateImageParallax();
    }
  }, [enable, updateImageParallax]);

  return {
    itemsRef,
    setCurrentPosition,
  };
}
